import Divider from '@mui/material/Divider';
import type { SxProps, Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { X } from '@phosphor-icons/react';
import type { ElementType, HTMLAttributes, ReactNode, SyntheticEvent } from 'react';
import { forwardRef } from 'react';
import { spacingTokens, text } from '../../theme/tokens/index.ts';
import { IconButton } from '../Button/index.ts';

/**
 * Panel — the shared "chrome" of every panel surface: an optional header (title + description +
 * close), a content region, and an optional footer action bar, plus toggleable dividers between
 * them. It is **surface-agnostic** — no background/border/radius of its own; the host provides the
 * surface (Dialog → MUI Dialog's Paper, Drawer → MUI Drawer's Paper). Single source of truth for
 * panel header/footer layout, dividers, spacing, and typography (Figma: Panel 1686:1080).
 *
 * Dividers are real MUI `Divider` elements (not CSS borders) so they can be toggled on/off with
 * `headerDivider` / `footerDivider` (both default `true`) — the MUI-idiomatic, Figma-aligned way.
 * Using `Divider` here does NOT change the global `MuiDivider` theme (styled locally).
 *
 * Footer layout: all footer buttons sit together in ONE group, aligned to a single side via
 * `footerAlign` (`'right'` default — Dialog; `'left'` — Drawer). Within the group the order is
 * always `secondaryActions` then `actions` (low-emphasis first, primary last), regardless of side.
 *
 * Composes MUI primitives only (Divider/Typography + brand IconButton/Button) — no new tokens.
 */
/**
 * Root is a plain `div` — `className`, `id`, `data-*` etc. pass through, plus `sx` for
 * per-instance styling. `title` shadows the native HTML attribute (here it is the heading slot).
 */
export interface PanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Heading (rendered `h2` by default — see `titleComponent`). Give it an id via `titleId` to wire the host's `aria-labelledby`. */
  title?: ReactNode;
  /**
   * Heading ELEMENT for `title` (visual style stays the `h2` typography variant). Set it so the
   * page's heading hierarchy stays consistent — e.g. `titleComponent="h3"` when the panel sits
   * under an existing h2. Default `'h2'`.
   */
  titleComponent?: ElementType;
  /** Secondary line under the title (rendered `body1` / `text.secondary`). */
  description?: ReactNode;
  /** Replace the entire default header (title/description/close) with custom content. */
  headerContent?: ReactNode;
  /** Primary footer actions — typically the primary `Button`(s). Rendered last in the group. */
  actions?: ReactNode;
  /** Low-emphasis footer actions (e.g. Cancel). Rendered first, before `actions`, on the same side. */
  secondaryActions?: ReactNode;
  /** Which side the footer button group sits on. `'right'` (default) for Dialog, `'left'` for Drawer. */
  footerAlign?: 'left' | 'right';
  /** Called by the ✕ close button. Rendered only when set (and `showCloseButton` is true). */
  onClose?: (event: SyntheticEvent) => void;
  /** Show the ✕ close button in the header. Default `true` (only rendered when `onClose` is set). */
  showCloseButton?: boolean;
  /** Accessible label for the close button. Default `"Close"`. */
  closeButtonLabel?: string;
  /** Show the divider below the header. Default `true`. */
  headerDivider?: boolean;
  /** Show the divider above the footer. Default `true`. */
  footerDivider?: boolean;
  /** id applied to the title element (host points `aria-labelledby` here). */
  titleId?: string;
  /** id applied to the description element (host points `aria-describedby` here). */
  descriptionId?: string;
  /** Panel body. Scrolls within the panel when it overflows. */
  children?: ReactNode;
  /** Per-instance styling of the root (styled `div`). */
  sx?: SxProps<Theme>;
}

const StyledPanelRoot = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
});

const StyledPanelHeader = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flexShrink: 0,
});

// Header row (Figma): title/description block on the left, close on the right.
// Padding pt sp16 / pb sp8 — the smaller bottom padding pairs with the divider's own sp8 margin.
const StyledHeaderRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: spacingTokens.sp12,
  padding: `${spacingTokens.sp16}px ${spacingTokens.sp24}px ${spacingTokens.sp8}px`,
});

const StyledHeaderText = styled('div')({
  flex: '1 1 auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: spacingTokens.sp2,
});

const StyledDescription = styled(Typography)({
  color: text.secondary,
});

// Toggleable divider — a real MUI Divider with sp8 margin above/below the 1px line (Figma
// Divider component). Styled locally so the global MuiDivider theme is untouched (SHARED).
const StyledPanelDivider = styled(Divider)({
  marginTop: spacingTokens.sp8,
  marginBottom: spacingTokens.sp8,
});

const StyledPanelBody = styled('div')({
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  padding: `${spacingTokens.sp16}px ${spacingTokens.sp24}px`,
});

const StyledPanelFooter = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flexShrink: 0,
});

// Footer row: one button group aligned to a single side (footerAlign). Padding pt sp8 / pb sp16 —
// the smaller top padding pairs with the divider's margin. `gap` spaces every button in the group.
const StyledFooterRow = styled('div', {
  shouldForwardProp: prop => prop !== 'align',
})<{ align: 'left' | 'right' }>(({ align }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacingTokens.sp8,
  justifyContent: align === 'left' ? 'flex-start' : 'flex-end',
  padding: `${spacingTokens.sp8}px ${spacingTokens.sp24}px ${spacingTokens.sp16}px`,
}));

export const Panel = forwardRef<HTMLDivElement, PanelProps>(function Panel(
  {
    title,
    titleComponent = 'h2',
    description,
    headerContent,
    actions,
    secondaryActions,
    footerAlign = 'right',
    onClose,
    showCloseButton = true,
    closeButtonLabel = 'Close',
    headerDivider = true,
    footerDivider = true,
    titleId,
    descriptionId,
    children,
    sx,
    ...rest
  },
  ref
) {
  const showClose = showCloseButton && Boolean(onClose);
  const hasHeader = Boolean(headerContent) || Boolean(title) || Boolean(description) || showClose;
  const hasFooter = Boolean(actions) || Boolean(secondaryActions);

  return (
    <StyledPanelRoot ref={ref} sx={sx} {...rest}>
      {hasHeader && (
        <StyledPanelHeader>
          <StyledHeaderRow>
            {headerContent ?? (
              <>
                <StyledHeaderText>
                  {title && (
                    <Typography id={titleId} variant="h2" component={titleComponent}>
                      {title}
                    </Typography>
                  )}
                  {description && (
                    <StyledDescription id={descriptionId} variant="body1">
                      {description}
                    </StyledDescription>
                  )}
                </StyledHeaderText>
                {showClose && (
                  <IconButton aria-label={closeButtonLabel} color="secondary" onClick={event => onClose?.(event)}>
                    <X />
                  </IconButton>
                )}
              </>
            )}
          </StyledHeaderRow>
          {headerDivider && <StyledPanelDivider />}
        </StyledPanelHeader>
      )}

      <StyledPanelBody>{children}</StyledPanelBody>

      {hasFooter && (
        <StyledPanelFooter>
          {footerDivider && <StyledPanelDivider />}
          <StyledFooterRow align={footerAlign}>
            {secondaryActions}
            {actions}
          </StyledFooterRow>
        </StyledPanelFooter>
      )}
    </StyledPanelRoot>
  );
});
